import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  styled,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Email,
  LocationOn,
  Phone,
  KeyboardArrowUp,
  YouTube,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import TikTokIcon from "../../../assets/icons/TikTokIcon";
import XIcon from "../../../assets/icons/XIcon";
import Logo from "../../../assets/images/logo.png";

const FooterContainer = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)",
  position: "relative",
  color: "#fff",
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)",
  },
}));

const FooterTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})(({ theme, isRtl }) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  color: theme.palette.common.white,
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: isRtl ? 'auto' : 0,
    right: isRtl ? 0 : 'auto',
    width: "30px",
    height: "2px",
    background: "linear-gradient(90deg, #FF4C00, #FF8A00)",
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.7)",
  textDecoration: "none",
  fontSize: "0.9rem",
  display: "block",
  marginBottom: theme.spacing(1.5),
  transition: "all 0.2s ease",
  "&:hover": {
    color: "#FF8A00",
    paddingLeft: "5px",
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  marginRight: theme.spacing(1.5),
  backgroundColor: "rgba(255, 255, 255, 0.05)",
  padding: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: ({ platform }) => {
      switch (platform) {
        case "Facebook":
          return "#1877F2";
        case "X":
          return "#000000";
        case "Instagram":
          return "#E4405F";
        case "YouTube":
          return "#FF0000";
        case "TikTok":
          return "#000000";
        default:
          return "#FF4C00";
      }
    },
    transform: "translateY(-2px)",
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  color: "rgba(255, 255, 255, 0.7)",
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1.5),
    color: "#FF4C00",
    fontSize: "1.2rem",
  },
}));

const BottomBar = styled(Box)(({ theme }) => ({
  borderTop: "1px solid rgba(255, 255, 255, 0.05)",
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(2),
  textAlign: "center",
}));

const MadeWithLove = styled(Typography)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "0.85rem",
  marginTop: theme.spacing(1),
  textAlign: "center",
  "& .heart": {
    color: "#FF4C00",
    display: "inline-block",
    animation: "heartBeat 1.5s infinite",
    margin: "0 2px",
  },
  "& a": {
    color: "rgba(255, 255, 255, 0.7)",
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#FF8A00",
    }
  },
  "@keyframes heartBeat": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.2)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
}));

const ScrollTopButton = styled(Button)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  minWidth: "40px",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  padding: 0,
  background: "linear-gradient(135deg, #FF4C00 0%, #FF8A00 100%)",
  color: "#fff",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  transition: "all 0.3s ease",
  zIndex: 1000,
  "&:hover": {
    background: "linear-gradient(135deg, #FF8A00 0%, #FF4C00 100%)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

const FooterLogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  position: "relative",
}));

const FooterLogo = styled("img")(({ theme }) => ({
  height: 60,
  width: "auto",
  objectFit: "contain",
  marginBottom: theme.spacing(2),
  filter: "brightness(1.05)",
  transition: "transform 0.3s ease",

  "&:hover": {
    transform: "translateY(-2px)",
  },

  [theme.breakpoints.up("md")]: {
    height: 70,
  },

  [theme.breakpoints.down("sm")]: {
    height: 50,
  },
}));

const FooterBrandName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.8rem",
  background: `linear-gradient(45deg, #FF4C00, #FF8A00)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginLeft: theme.spacing(1),
  lineHeight: 1.2,

  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
  },
}));

const FooterDescription = styled(Typography)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.7)",
  marginBottom: theme.spacing(3),
  maxWidth: "300px",
  lineHeight: 1.6,
  fontSize: "0.95rem",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -theme.spacing(2),
    left: 0,
    width: "60px",
    height: "2px",
    background: "linear-gradient(90deg, #FF4C00, #FF8A00)",
    borderRadius: theme.shape.borderRadius,
  },
}));

const Footer = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  
  // Check if the language is RTL (Pashto or Dari)
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  // Quick Links Data
  const quickLinks = {
    column1: [
      { title: t("footer.links.aboutUs"), path: "/about" },
      { title: t("footer.links.team"), path: "/team" },
      { title: t("footer.links.faqs"), path: "/help/faqs" },
      { title: t("footer.links.contact"), path: "/help/contact-us" },
    ],
    column2: [
      { title: t("footer.links.terms"), path: "/help/terms" },
      { title: t("footer.links.privacy"), path: "/help/privacy-policy" },
      { title: t("footer.links.returns"), path: "/help/returns-policy" },
    ],
  };

  // Social Media Links
  const socialLinks = [
    {
      label: "Facebook",
      url: "https://www.facebook.com/share/1BsQLxJvwN/?mibextid=wwXIfr",
      icon: <Facebook />,
    },
    {
      label: "X",
      url: "https://x.com/hasabookstore?s=21&t=wP7EbJum52YKsFF6uZiLZg",
      icon: <XIcon />,
    },
    {
      label: "Instagram",
      url: "https://www.instagram.com/hasa_book_store?igsh=eGh5NzJxZ25uYzR6",
      icon: <Instagram />,
    },
    {
      label: "YouTube",
      url: "https://www.youtube.com/@hasacircle",
      icon: <YouTube />,
    },
    {
      label: "TikTok",
      url: "https://www.tiktok.com/@hasa_books_store?_t=ZN-8xZxBfIyPHq&_r=1",
      icon: <TikTokIcon />,
    },
  ];

  // Contact Information
  const contactInfo = {
    address: t("footer.contact.address"),
    email: "support@hasabookstore.com",
    phone: "+380 73 642 13 67",
  };

  // Scroll to Top Handler
  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Show/Hide Scroll Button based on scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Social Media Click Handler
  const handleSocialClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Contact Click Handlers
  const handleEmailClick = () => {
    window.location.href = `mailto:${contactInfo.email}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleLocationClick = () => {
    window.open(
      "https://maps.google.com/?q=" + encodeURIComponent(contactInfo.address),
    );
  };

  return (
    <FooterContainer>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info - Updated Section */}
          <Grid item xs={12} md={4}>
            <FooterLogoContainer>
              <Link to="/" style={{ textDecoration: "none" }}>
                <FooterLogo
                  src={Logo}
                  alt="Hasa Book Store"
                  loading="lazy"
                  width="48"
                  height="48"
                  draggable="false"
                />
              </Link>
              <FooterBrandName component="h2">{t("footer.brandName")}</FooterBrandName>
            </FooterLogoContainer>

            <FooterDescription>
              {t("footer.description")}
            </FooterDescription>

            <Box
              sx={{
                mb: 4,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              {socialLinks.map((social, index) => (
                <Tooltip key={index} title={social.label}>
                  <SocialIconButton
                    aria-label={social.label}
                    onClick={() => handleSocialClick(social.url)}
                    platform={social.label}
                  >
                    {social.icon}
                  </SocialIconButton>
                </Tooltip>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <FooterTitle isRtl={isRtl}>{t("footer.quickLinks")}</FooterTitle>
            <Grid container>
              <Grid item xs={6}>
                {quickLinks.column1.map((link, index) => (
                  <FooterLink key={index} to={link.path}>
                    {link.title}
                  </FooterLink>
                ))}
              </Grid>
              <Grid item xs={6}>
                {quickLinks.column2.map((link, index) => (
                  <FooterLink key={index} to={link.path}>
                    {link.title}
                  </FooterLink>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={4}>
            <FooterTitle isRtl={isRtl}>{t("footer.contactUs")}</FooterTitle>
            <ContactItem
              onClick={handleLocationClick}
              sx={{ cursor: "pointer" }}
            >
              <LocationOn />
              <Typography variant="body2">{contactInfo.address}</Typography>
            </ContactItem>
            <ContactItem onClick={handleEmailClick} sx={{ cursor: "pointer" }}>
              <Email />
              <Typography variant="body2">{contactInfo.email}</Typography>
            </ContactItem>
            <ContactItem onClick={handlePhoneClick} sx={{ cursor: "pointer" }}>
              <Phone />
              <Typography variant="body2">{contactInfo.phone}</Typography>
            </ContactItem>
          </Grid>
        </Grid>

        <BottomBar>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.5)" }}
          >
            © {currentYear} {t("footer.copyright")}
          </Typography>
          <MadeWithLove>
            {t("footer.madeWithLove").split("❤")[0]}
            <span className="heart">❤</span>
            <a href="https://urooj.tech" target="_blank" rel="noopener noreferrer">
              {t("footer.madeWithLove").split("❤")[1]}
            </a>
          </MadeWithLove>
        </BottomBar>
      </Container>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Tooltip title={t("footer.scrollToTop")}>
          <ScrollTopButton onClick={handleScrollTop} aria-label="scroll to top">
            <KeyboardArrowUp />
          </ScrollTopButton>
        </Tooltip>
      )}
    </FooterContainer>
  );
};

export default Footer;
